import {makeAutoObservable, observable, runInAction} from 'mobx'
import {Lang} from 'yougile-lang'
import {ObjUtil, Util} from 'yougile-util'
import {
  findCurrentIntervalState,
  Hub,
  SprintStickerState,
  StickerIcon,
  stickerIconProperty,
  StickerStateId,
  StringStickerState,
  Tag
} from 'yougile-model'
import {Sync} from '@client/sync'
import {Tracking} from '@common/tracking'
import {TrackingEvent} from '@common/tracking-events'
import {BoardSettingsStore} from '@components/board/board-settings/board-settings-store'
import {StickerSettingsState} from '@components/sticker/my-stickers/board-stickers/sticker-settings/types'
import {
  MyStickersStore,
  NewStickerType
} from '@components/sticker/my-stickers/my-stickers-store'
import {getStickerIconByNewStickerType} from '@components/sticker/my-stickers/my-stickers-utils'
import {createUseStore} from '@containers/hooks'
import {globals} from '@extensions/globals-base'
import {PageService} from '@extensions/page-service'
import {BoardFilters} from '@extensions/sticker/board-filters'
import {Sticker, stickerCtl} from '@extensions/sticker/sticker'
import {reorderStates} from './sticker-editor-utils'

const stringCtl = stickerCtl.String
const sprintCtl = stickerCtl.Sprint
const stickersWithIcon = ['string', 'text', 'numeric'] as readonly string[]

type FormFields = 'name' | 'icon'
type FormErrors = Readonly<{[k in FormFields]?: string}>

export interface StickerEditorState {
  notSavedError?: boolean
  formErrors: FormErrors
  errorBg: boolean
  initialSticker?: Tag
  initialSettings?: StickerSettingsState
  sticker?: Tag
  stickerIcon?: StickerIcon
  stickerName: string
  stickerType: NewStickerType
  stickerEdited: boolean
  stickerChanged: boolean
  stickerSettings: StickerSettingsState
  editCurrentState: boolean
  stringStates: StringStickerState[]
  stringState?: StringStickerState
  stringStateDragged?: StringStickerState
  sprintStates: SprintStickerState[]
  sprintState?: SprintStickerState
  stringStateName: string
  sprintStateName: string
  initialSprintBegin?: number
  initialSprintEnd?: number
}

export class StickerEditorStore {
  state: StickerEditorState = {
    sticker: undefined,
    stickerIcon: undefined,
    stickerName: '',
    stickerType: 'string',
    stickerEdited: false,
    stickerChanged: false,
    stickerSettings: {
      hasDefaultState: false,
      defaultState: ''
    },
    editCurrentState: false,
    stringStates: [],
    stringState: undefined,
    stringStateDragged: undefined,
    sprintStates: [],
    sprintState: undefined,
    stringStateName: '',
    sprintStateName: '',
    initialSprintBegin: undefined,
    initialSprintEnd: undefined,
    formErrors: {},
    errorBg: false
  }

  constructor(
    public myStickersStore: MyStickersStore,
    public boardSettingsStore: BoardSettingsStore
  ) {
    makeAutoObservable(
      this,
      {
        state: observable.shallow,
        myStickersStore: observable.ref,
        boardSettingsStore: observable.ref
      },
      {autoBind: true}
    )
  }

  setStickerIcon(icon: StickerIcon): void {
    this.state.stickerIcon = icon
    this.clearFieldError('icon')
  }

  setStickerName(name: string): void {
    this.state.stickerName = name
    this.clearFieldError('name')
  }

  setStickerType(type: NewStickerType): void {
    this.state.stickerType = type
  }

  updateStickerSettings(
    dataOrUpdate:
      | StickerSettingsState
      | ((settings: StickerSettingsState) => StickerSettingsState)
  ): void {
    this.state.stickerSettings =
      typeof dataOrUpdate === 'function'
        ? dataOrUpdate(this.state.stickerSettings)
        : dataOrUpdate
  }

  init(): void {
    this.boardSettingsStore.setOnHasChangesCb(() => {
      runInAction(() => {
        this.state.notSavedError = true
        this.blinkErrorBg()
      })
    })
  }

  initForNewSticker(type: NewStickerType): void {
    this.init()

    const box = Sync.getCurrentSandbox()
    this.state.stickerType = type
    this.state.sticker = Sticker.createStickerByType(type, box)
    this.setStickerIcon(getStickerIconByNewStickerType(type) as StickerIcon)
    this.state.stickerSettings = {}
    this.state.initialSettings = {}
    this.state.initialSticker = undefined
  }

  initForExistingSticker(existingSticker: Tag): void {
    this.init()

    const board = PageService.getCurrentBoard()
    const initialSettings = board?.data?.stickersSettings?.[existingSticker.id]
    this.state.initialSettings = Util.clone(initialSettings) ?? {}
    const initialSticker = new Tag(
      existingSticker.companyId,
      existingSticker.title
    )
    initialSticker.fromJSON(existingSticker.companyId, existingSticker.toJSON())
    this.state.initialSticker = initialSticker

    const sticker = new Tag(existingSticker.companyId, existingSticker.title)
    sticker.fromJSON(existingSticker.companyId, existingSticker.toJSON())
    this.state.sticker = sticker
    this.state.stickerSettings = Util.clone(this.state.initialSettings)

    this.setStickerIcon(Sticker.getIcon(existingSticker) as StickerIcon)
    this.setStickerName(Sticker.getName(existingSticker))
    this.setStickerType(Sticker.getType(existingSticker) as NewStickerType)
    if (Sticker.getType(existingSticker) === 'string') {
      this.setStringStates(stickerCtl.String.listStates(existingSticker))
    }
    if (Sticker.getType(existingSticker) === 'sprint') {
      this.setSprintStates(stickerCtl.Sprint.listStates(existingSticker))
    }
  }

  setStickerEdited(value: boolean): void {
    this.state.stickerEdited = value
  }

  setStickerChanged(value: boolean): void {
    this.state.stickerChanged = value
  }

  setEditCurrentState(value: boolean): void {
    this.state.editCurrentState = value
  }

  setStringState(stickerState?: StringStickerState): void {
    this.state.stringState = stickerState
  }

  setStringStates(listStates: StringStickerState[]): void {
    this.state.stringStates = listStates
    this.fixDefaultState(listStates)
  }

  setSprintState(sprintState?: SprintStickerState): void {
    this.state.sprintState = sprintState
    if (!this.state.initialSprintBegin && !this.state.initialSprintEnd) {
      this.state.initialSprintBegin = sprintState?.begin
      this.state.initialSprintEnd = sprintState?.end
    }
  }

  setSprintStates(listStates: SprintStickerState[]): void {
    this.state.sprintStates = listStates
    this.fixDefaultState(listStates)
  }

  // Fix default state in settings if a current default state is removed from states
  private fixDefaultState(states: {id: StickerStateId}[]): void {
    if (this.state.stickerSettings.hasDefaultState) {
      const defaultState = this.state.stickerSettings.defaultState as string
      if (defaultState !== 'no-default-state') {
        const idx = states.findIndex(x => x.id === defaultState)
        if (idx === -1) {
          this.state.stickerSettings = {
            ...this.state.stickerSettings,
            defaultState: 'no-default-state'
          }
        }
      }
    }
  }

  stringStateDragEnter(stringStickerState: StringStickerState): void {
    const from = this.state.stringStateDragged?.id
    if (!from || from === stringStickerState.id || !this.state.sticker) {
      return
    }
    const order = Util.clone(
      this.state.sticker.states.order
    ) as StickerStateId[]
    reorderStates(from, stringStickerState.id, {
      sticker: this.state.sticker,
      fromOrder: order,
      shouldCommit: true
    })
    this.setStringStates(stringCtl.listStates(this.state.sticker))
  }

  reset(): void {
    this.resetForm()

    runInAction(() => {
      this.boardSettingsStore.setHasChanges(false)
      this.boardSettingsStore.setOnHasChangesCb(undefined)
    })
  }

  private resetForm(): void {
    this.state.sticker = undefined
    this.state.stickerName = ''
    this.state.stickerIcon = undefined
    this.state.stickerSettings = {}
    this.state.formErrors = {}
    this.state.errorBg = false
    this.state.notSavedError = false
    this.boardSettingsStore.setHasChanges(false)
  }

  private save(): void {
    const {sticker, initialSticker} = this.state
    if (!sticker) {
      return
    }
    const board = PageService.getCurrentBoard()

    if (initialSticker) {
      const box = Sync.getCurrentSandbox()
      const existingSticker = box.getTag(initialSticker.id)
      if (!existingSticker) {
        return
      }
      if (!Sticker.isSystem(existingSticker)) {
        existingSticker.fromJSON(existingSticker.companyId, sticker.toJSON())
        existingSticker.title = this.state.stickerName.trim()
        existingSticker.properties[stickerIconProperty] =
          this.state.stickerIcon || ''
        existingSticker.commit()
      }
      this.afterSave(board, existingSticker)
    } else {
      sticker.title = this.state.stickerName.trim()
      sticker.properties[stickerIconProperty] = this.state.stickerIcon || ''
      Sticker.add(Sync.getCurrentSandbox(), sticker)
      Sticker.addToBoard(board, sticker)
      void Tracking.send(TrackingEvent.stickerCreate)
      this.afterSave(board, sticker)
    }

    board.data.stickersSettings ??= {}
    const stickerId = initialSticker?.id ?? sticker.id
    board.data.stickersSettings[stickerId] = this.state.stickerSettings
    board.commit()
  }

  private afterSave(board: Hub, sticker: Tag): void {
    const type = Sticker.getType(sticker)
    if (type === 'sprint') {
      if (this.state.stickerSettings.filterByCurrentValue ?? true) {
        this.addToBoardSprintAutoFilter(board, sticker)
      } else {
        this.removeFromBoardSprintAutoFilter(board, sticker)
      }
    }
  }

  stringStateDragStart(stringStickerState: StringStickerState): void {
    this.state.stringStateDragged = stringStickerState
  }

  stringStateDragEnd(): void {
    this.state.stringStateDragged = undefined
  }

  addSprintState(): void {
    this.setStickerEdited(true)
    this.setSprintState(sprintCtl.makeNewState())
  }

  addStringState(): void {
    this.setStickerEdited(true)
    this.setStringState(stringCtl.makeNewState())
  }

  saveStringState(opts?: {openNewEditAfterSave?: boolean}): void {
    if (
      !this.state.stringStateName ||
      !this.state.sticker ||
      !this.state.stringState
    ) {
      this.cancelStringState()
      return
    }
    this.setStickerChanged(true)
    const st = {
      ...this.state.stringState,
      name: this.state.stringStateName.trim()
    }
    if (this.state.editCurrentState) {
      stringCtl.changeState(this.state.sticker, this.state.stringState.id, st)
      this.setStringStates(stringCtl.listStates(this.state.sticker))
      this.setEditCurrentState(false)
    } else {
      stringCtl.addState(this.state.sticker, st)
      this.setStickerEdited(false)
    }
    this.setStringStateName('')
    opts?.openNewEditAfterSave && this.addStringState()
  }

  saveSprintState(opts?: {openNewEditAfterSave?: boolean}): void {
    if (!this.state.sticker || !this.state.sprintState) {
      this.cancelSprintState()
      return
    }
    if (!this.state.sprintStateName) {
      if (
        this.state.sprintState.begin === this.state.initialSprintBegin &&
        this.state.sprintState.end === this.state.initialSprintEnd
      ) {
        this.cancelSprintState()
        return
      }
      this.setSprintStateName(Lang.msg.undefined)
    }
    this.setStickerChanged(true)
    const st = {
      ...this.state.sprintState,
      name: this.state.sprintStateName.trim()
    }
    if (this.state.editCurrentState) {
      sprintCtl.changeState(this.state.sticker, this.state.sprintState.id, st)
      this.setSprintStates(sprintCtl.listStates(this.state.sticker))
      this.setEditCurrentState(false)
    } else {
      sprintCtl.addState(this.state.sticker, st)
      this.setSprintStates(sprintCtl.listStates(this.state.sticker))
      this.setStickerEdited(false)
    }
    this.setSprintStateName('')
    this.state.initialSprintBegin = undefined
    this.state.initialSprintEnd = undefined
    opts?.openNewEditAfterSave && this.addSprintState()
  }

  cancelStringState(): void {
    this.setStringState(undefined)
    this.setStickerEdited(false)
    this.setEditCurrentState(false)
  }

  setStringStateName(name: string): void {
    this.state.stringStateName = name
  }

  cancelSprintState(): void {
    this.setSprintState(undefined)
    this.setStickerEdited(false)
    this.setEditCurrentState(false)
    this.state.initialSprintBegin = undefined
    this.state.initialSprintEnd = undefined
  }

  setSprintStateName(name: string): void {
    this.state.sprintStateName = name
  }

  checkEdited(): void {
    this.boardSettingsStore.setHasChanges(this.edited)
  }

  get edited(): boolean {
    let edited = false
    const initialSticker = this.state.initialSticker
    if (initialSticker) {
      edited ||= Sticker.getName(initialSticker) !== this.state.stickerName
      if (stickersWithIcon.includes(Sticker.getType(initialSticker))) {
        edited ||= Sticker.getIcon(initialSticker) !== this.state.stickerIcon
      }
      if (Sticker.getType(initialSticker) === 'string') {
        const states = stringCtl.listStates(initialSticker)
        edited ||= !ObjUtil.deepEqual(states, this.state.stringStates, true)
      }
      if (Sticker.getType(initialSticker) === 'sprint') {
        const states = sprintCtl.listStates(initialSticker)
        edited ||= !ObjUtil.deepEqual(states, this.state.sprintStates, true)
      }
    } else {
      edited ||= !!this.state.stickerName
      const stickerType = this.state.stickerType
      if (stickersWithIcon.includes(stickerType)) {
        const defaultIcon = getStickerIconByNewStickerType(stickerType)
        const currentIcon = this.state.stickerIcon
        edited ||= !!currentIcon && currentIcon !== defaultIcon
      }
      if (stickerType === 'string') {
        edited ||= !!this.state.stringStates.length
      }
      if (stickerType === 'sprint') {
        if (this.state.sprintStates.length === 2) {
          // Compare with default state from SprintCtl#make
          edited ||=
            this.state.sprintStates[0].name !== 'Sprint 1' &&
            this.state.sprintStates[1].name !== 'Backlog'
        } else {
          edited ||= !!this.state.sprintStates.length
        }
      }
    }
    edited ||= !ObjUtil.deepEqual(
      StickerEditorStore.settingsToComparable(this.state.initialSettings),
      StickerEditorStore.settingsToComparable(this.state.stickerSettings),
      true
    )

    return edited
  }

  get hasErrors(): boolean {
    return (
      this.state.notSavedError || !!Object.keys(this.state.formErrors).length
    )
  }

  get errorText(): string | undefined {
    if (this.state.notSavedError) {
      return Lang.msg.errorNotSaveData
    }
    return Object.values(this.state.formErrors)[0]
  }

  private setFieldError(field: FormFields, error: string): void {
    const errors = {...this.state.formErrors}
    errors[field] = error
    this.state.formErrors = errors
    this.blinkErrorBg()
  }

  private clearFieldError(field: FormFields): void {
    if (this.fieldHasError(field)) {
      const errors = {...this.state.formErrors}
      delete errors[field]
      this.state.formErrors = errors
    }
    this.state.errorBg = false
    this.state.notSavedError = false
  }

  fieldHasError(field: FormFields): boolean {
    return field in this.state.formErrors
  }

  validate(): boolean {
    if (!this.state.stickerName.trim().length) {
      const langKey = this.state.initialSticker
        ? 'noNameOnEditWarning'
        : 'noNameOnCreateWarning'
      this.setFieldError('name', Lang.msg.myStickers[langKey])
      return false
    }

    if (this.state.stickerName.trim().startsWith('.')) {
      this.setFieldError('name', Lang.msg.myStickers.nameStartsWithDotError)
      return false
    }

    return true
  }

  trySave(): void {
    if (this.validate()) {
      this.save()
      this.resetForm()
      this.myStickersStore.setCurrentTab('board')
    }
  }

  cancel(): void {
    this.myStickersStore.setCurrentTab('board')
  }

  tryGoBack(): void {
    if (this.edited) {
      this.state.notSavedError = true
      this.blinkErrorBg()
    } else {
      this.myStickersStore.goBack()
    }
  }

  private addToBoardSprintAutoFilter(board: Hub, sticker: Tag): void {
    if (!globals.boardsAutoFiltered.has(board.id)) {
      globals.boardsAutoFiltered.add(board.id)
    }

    const states = stickerCtl.Sprint.listStates(sticker)
    const state = findCurrentIntervalState(states)
    if (state) {
      const cur = BoardFilters.getForSticker(board, sticker)
      if (!cur.includes(state.id)) {
        cur.push(state.id)
      }
      BoardFilters.setFilter(board, sticker, cur)
      BoardFilters.save()
    }
  }

  private removeFromBoardSprintAutoFilter(board: Hub, sticker: Tag): void {
    if (BoardFilters.current?.[board.id]?.[sticker.id]) {
      delete BoardFilters.current[board.id][sticker.id]
      BoardFilters.save()
    }

    if (globals.boardsAutoFiltered.has(board.id)) {
      if (Object.values(BoardFilters.current?.[board.id] ?? {}).length > 0) {
        globals.boardsAutoFiltered.delete(board.id)
      }
    }
  }

  private blinkErrorBg(): void {
    this.state.errorBg = true
    setTimeout(() => runInAction(() => (this.state.errorBg = false)), 300)
  }

  private static settingsToComparable(
    state: StickerSettingsState | undefined
  ): StickerSettingsState {
    const hasDefaultState = state?.hasDefaultState ?? false
    let defaultState: undefined | string | string[] = undefined
    if (hasDefaultState && state?.defaultState) {
      defaultState =
        typeof state.defaultState === 'string'
          ? state.defaultState
          : [...state.defaultState]
    }
    const filterByCurrentValue = state?.filterByCurrentValue ?? true
    const shouldResetOnMove = state?.shouldResetOnMove ?? false
    const shouldStopWhenCompleted = state?.shouldStopWhenCompleted ?? false
    const required = state?.required ?? 'none'
    const titleBeforeValue = state?.titleBeforeValue ?? false
    const showNumericStickersSum = state?.showNumericStickersSum ?? false
    return {
      hasDefaultState,
      defaultState,
      filterByCurrentValue,
      shouldResetOnMove,
      shouldStopWhenCompleted,
      required,
      titleBeforeValue,
      showNumericStickersSum
    }
  }
}

const [StickerEditorContext, useStickerEditorStore] =
  createUseStore(StickerEditorStore)
export {StickerEditorContext, useStickerEditorStore}
